/**
 * Phase 27.37.C — Public A2P verification page.
 *
 * Twilio/TCR reviewers open this URL directly to see the actual
 * SMS opt-in workflow used across KR Agency Hub lead/contact forms.
 * No authentication required, no data written on submit — this is a
 * demonstration surface. All the real lead forms embed the SAME
 * checkbox and disclosure text (see PublicLandingPage.jsx).
 *
 * Route: /sms-opt-in
 */

import React, { useState } from 'react';
import { formatPhoneInput } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';

export default function PublicSmsOptInPage() {
  const [phone, setPhone] = useState('');
  const [smsAccepted, setSmsAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // This is a demonstration surface — no data leaves the browser.
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-2xl mx-auto p-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">KR Enterprise Group — SMS Opt-In</h1>
          <p className="text-sm text-slate-600 mt-2">
            This page demonstrates the SMS opt-in workflow used across every KR
            Agency Hub public lead and contact form. It is provided for A2P /
            TCR review — no data is stored when submitted here.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          {submitted ? (
            <div className="text-center py-8" data-testid="sms-opt-in-thanks">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <h2 className="text-lg font-semibold text-slate-900">Thanks — demo received</h2>
              <p className="text-sm text-slate-600 mt-1">
                Your submission was not stored. Return to the{' '}
                <Link className="text-sky-600 underline" to="/">homepage</Link>.
              </p>
              {smsAccepted && (
                <p className="text-xs text-slate-500 mt-4 max-w-md mx-auto">
                  You ticked the SMS opt-in checkbox. In production this would
                  create an audit record with the exact disclosure wording,
                  phone number, timestamp, and source form.
                </p>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="sms-opt-in-form">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Mobile phone number</label>
                <input
                  type="tel" required
                  value={formatPhoneInput(phone)} onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
                  placeholder="+1 (555) 123-4567"
                  data-testid="sms-opt-in-phone"
                />
              </div>

              <div className="border-t border-slate-100 pt-4">
                <label className="flex items-start gap-3 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 accent-slate-900"
                    checked={smsAccepted}
                    onChange={(e) => setSmsAccepted(e.target.checked)}
                    data-testid="sms-opt-in-checkbox"
                  />
                  <span className="text-slate-700 leading-relaxed">
                    I agree to receive SMS/MMS messages from KR Enterprise
                    Group and its authorized representatives regarding my
                    inquiry, requested insurance information, appointments,
                    application or service updates, and customer support.{' '}
                    <strong>Message frequency varies.</strong>{' '}
                    <strong>Message and data rates may apply.</strong>{' '}
                    Reply <strong>STOP</strong> to opt out or{' '}
                    <strong>HELP</strong> for assistance.{' '}
                    <strong>Consent is not a condition of purchase.</strong>
                  </span>
                </label>
                <div className="mt-2 ml-6 text-[11px] text-slate-500">
                  <a
                    href="https://krenterprisegroup.app/privacy"
                    target="_blank" rel="noreferrer"
                    className="underline hover:text-slate-800"
                    data-testid="sms-opt-in-privacy-link"
                  >
                    Privacy Policy
                  </a>
                  {' · '}
                  <a
                    href="https://krenterprisegroup.app/terms"
                    target="_blank" rel="noreferrer"
                    className="underline hover:text-slate-800"
                    data-testid="sms-opt-in-terms-link"
                  >
                    Terms &amp; Conditions
                  </a>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-slate-900 text-white rounded-lg py-2.5 font-medium hover:bg-slate-800 transition"
                data-testid="sms-opt-in-submit"
              >
                Submit
              </button>

              <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
                The SMS opt-in checkbox is <strong>optional</strong> and{' '}
                <strong>not pre-checked</strong>. You can submit this form
                without agreeing to SMS. Ticking the box creates a permanent,
                timestamped consent record with the exact disclosure text
                you agreed to.
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
